import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, LogIn } from 'lucide-react';
import { useAuth } from '../lib/auth';
import { completeAuth0SignIn, isAuth0Callback, signInOptions, startAuth0SignIn, type SignInOptions } from '../lib/auth0';
import { Button } from '../components/ui/button';
import { ThemeToggle } from '../components/ui/theme-toggle';

export default function Login() {
  const { loginWithAuth0 } = useAuth();
  const navigate = useNavigate();

  const [options, setOptions] = useState<SignInOptions | null>(null);
  const [auth0Busy, setAuth0Busy] = useState(isAuth0Callback());
  const [error, setError] = useState<string | null>(null);
  const handledCallback = useRef(false);

  useEffect(() => {
    void signInOptions().then(setOptions);
  }, []);

  // Auth0 hands the browser back here with ?code&state. Finish that login and trade the token for
  // the console session — once, because React's strict mode mounts effects twice in development and
  // an authorization code is single-use.
  useEffect(() => {
    if (!isAuth0Callback() || handledCallback.current) return;
    handledCallback.current = true;
    (async () => {
      try {
        await loginWithAuth0(await completeAuth0SignIn());
        navigate('/', { replace: true });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Auth0 sign-in failed');
        setAuth0Busy(false);
      }
    })();
  }, [loginWithAuth0, navigate]);

  async function handleAuth0() {
    setError(null);
    setAuth0Busy(true);
    try {
      await startAuth0SignIn();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not reach Auth0');
      setAuth0Busy(false);
    }
  }

  return (
    <div className="min-h-screen bg-surface flex items-center justify-center p-4">
      <div className="fixed top-4 right-4">
        <ThemeToggle />
      </div>
      <div className="w-full max-w-sm">

        {/* Brand */}
        <div className="flex flex-col items-center mb-8">
          <div className="w-12 h-12 rounded-2xl bg-orange-500 flex items-center justify-center shadow-lg shadow-orange-200 mb-4">
            <span className="text-white text-lg font-bold">C</span>
          </div>
          <h1 className="text-xl font-bold text-ink">CDR Memory</h1>
          <p className="text-sm text-ink-muted mt-0.5">Review Console</p>
        </div>

        {/* Card */}
        <div className="bg-card rounded-2xl border border-border shadow-sm p-8">
          <h2 className="text-lg font-semibold text-ink mb-1">Welcome back</h2>
          <p className="text-sm text-ink-muted mb-6">Sign in to your account to continue</p>

          {options === null ? (
            <div className="flex justify-center py-2">
              <Loader2 size={16} className="animate-spin text-ink-faint" />
            </div>
          ) : options.auth0 ? (
            <Button
              type="button"
              variant="primary"
              onClick={() => void handleAuth0()}
              disabled={auth0Busy}
              className="w-full justify-center py-2.5"
            >
              {auth0Busy ? <Loader2 size={15} className="animate-spin" /> : <LogIn size={15} />}
              {auth0Busy ? 'Signing you in…' : 'Continue with Auth0'}
            </Button>
          ) : (
            // Auth0 is the only way in, so a deployment without it has no sign-in at all — saying so
            // beats a button that fails.
            <p className="text-sm text-ink-muted">
              Sign-in isn't configured on this deployment. Ask an administrator to set up Auth0.
            </p>
          )}

          {error && (
            <div className="mt-4 rounded-lg bg-red-50 dark:bg-red-950/30 border border-red-100 dark:border-red-900 px-3 py-2.5">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
